import { Injectable } from '@angular/core';
import { catchError, Observable } from 'rxjs';
import { PaginationMetaDto } from '../dto';
import { ApiCrudService } from '../interfaces/api-crud-service.interface';
import { ApiOptions, ApiParams } from '../interfaces/api-options.interface';
import { PaginatedListQuery } from '../interfaces/paginated-list-query.interface';
import { toPaginationParams } from '../utils/pagination.utils';
import { AbstractApiService } from './abstract-api.service';
/**
 * Abstract API object.
 * Extend this class to get the default C.R.U.D. API configuration.
 */
@Injectable()
export abstract class AbstractCrudApiService<Dto, CreateDto, UpdateDto> extends AbstractApiService implements ApiCrudService {
  /**
   * Creates a new resource or multiple resources using the provided data transfer object (DTO).
   *
   * @param {Partial<CreateDto> | Partial<CreateDto>[]} dto - A single or array of DTOs containing the data to create the resource(s).
   * @param {ApiParams} [params] - Optional parameters to include in the request, such as query parameters or URL path variables.
   * @param {ApiOptions} [options] - Optional API request options, such as headers or configurations.
   * @return {Observable<Partial<Dto>>} An observable that emits the created resource(s) or an error upon failure.
   */
  create(dto: Partial<CreateDto> | Partial<CreateDto>[], params?: ApiParams, options?: ApiOptions): Observable<Dto> {
    const url: string = this.url(params);
    return this.http.post<Dto>(url, dto, options).pipe(catchError(error => this.handleHttpErrorResponse(error)));
  }

  /**
   * Reads a list of resources.
   *
   * The shape of the response is driven by the presence of a `pagination` query:
   *  - omit it  -> full list `Dto[]`;
   *  - provide it -> a server page `{ data, meta }` (page/limit/sort/search are
   *    turned into query params automatically).
   *
   * @param {ApiParams} [params] Path/filter params used to build the URL.
   * @param {ApiOptions} [options] HTTP options, optionally carrying a `pagination` query.
   */
  read(params?: ApiParams, options?: ApiOptions): Observable<Dto[]>
  read(params: ApiParams | undefined, options: ApiOptions & { pagination: PaginatedListQuery }): Observable<{ data: Dto[], meta: PaginationMetaDto }>
  read(params?: ApiParams, options?: ApiOptions & { pagination?: PaginatedListQuery }): Observable<Dto[] | { data: Dto[], meta: PaginationMetaDto }> {
    const url = this.url(params);

    const { pagination, ...httpOptions } = options ?? {};
    const finalOptions: ApiOptions = pagination
      ? { ...httpOptions, params: { ...httpOptions.params, ...toPaginationParams(pagination) } }
      : httpOptions;

    return this.http.get<Dto[] | { data: Dto[], meta: PaginationMetaDto }>(url, finalOptions).pipe(
      catchError(error => this.handleHttpErrorResponse(error))
    );
  }

  /**
   * Fetches a single resource based on the provided parameters.
   *
   * @param {string|number|Record<string, string | number>} params - Parameters used to identify the specific resource. This could be a string, number, or an object with key-value pairs.
   * @param {ApiOptions} [options] - Optional configuration for the HTTP request, such as headers or query parameters.
   * @return {Observable<Dto>} Observable that emits the requested resource data.
   */
  readOne(params: ApiParams, options?: ApiOptions): Observable<Dto> {
    const url: string = this.url(params);
    return this.http.get<Dto>(url, options).pipe(
      catchError(error => this.handleHttpErrorResponse(error)));
  }

  /**
   * Updates the resource with the given parameters, data transfer object, and optional API options.
   *
   * @param {ApiParams} params - The identifier or query parameters used to locate the resource.
   * @param {Partial<UpdateDto> | Partial<UpdateDto>[]} dto - The partial Data Transfer Object(s) containing the update information.
   * @param {ApiOptions} [options] - Optional configuration for the API request.
   * @return {Observable<Partial<Dto>>} An observable emitting the updated resource or partial DTO.
   */
  update(params: ApiParams, dto: Partial<UpdateDto> | Partial<UpdateDto>[], options?: ApiOptions): Observable<Partial<Dto>> {
    const url: string = this.url(params);
    return this.http.put<Dto>(url, dto, options).pipe(
      catchError(error => this.handleHttpErrorResponse(error)));
  }

  /**
   * Deletes a resource identified by the given parameters.
   *
   * @param {ApiParams} params - The identifier or criteria to locate the resource to be deleted.
   * @param {ApiOptions} [options] - Optional configuration for the HTTP request.
   * @return {Observable<void>} An observable emitting the partial representation of the deleted resource.
   */
  delete(params: ApiParams, options?: ApiOptions): Observable<Dto> {
    const url: string = this.url(params);
    return this.http.delete<Dto>(url, options).pipe(
      catchError(error => this.handleHttpErrorResponse(error)));
  }

  /**
   * Restores a resource by sending a POST request to the specified URL.
   *
   * @param {ApiParams} params - Parameters to construct the URL for the restore operation. Can be a string, number, or an object containing key-value pairs.
   * @param {ApiOptions} [options] - Additional options to be passed with the restore request.
   * @return {Observable<void>} An observable that completes when the restore operation is successful or emits an error if the operation fails.
   */
  restore(params: ApiParams, options?: ApiOptions): Observable<Dto> {
    const url: string = this.url(params);
    // `post(url, body, options)` — restore has no body, so pass null; `options`
    // must go in the 3rd slot or it would be serialized as the request body.
    return this.http.post<Dto>(`${url}/restore`, null, options).pipe(
      catchError(error => this.handleHttpErrorResponse(error)));
  }
}
